'use client';

import { ExternalLink, MoreHorizontal } from 'lucide-react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { ChevronRightIcon } from '@radix-ui/react-icons';
import { NavDef } from '@/hooks/useNavGroups';
import {
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenu,
  DropdownMenuPortal,
} from './ui/dropdown-menu';
import { NavLink, useRouteMatch, matchPath } from 'react-router-dom';
import { Badge } from './ui/badge';
import { CompanyAlerts, useGetCompanyAlertsQuery } from '@/generated/codegen';
import { useOptionalFilterState } from '@/hooks/useFilter';

export function NavMain({ groups }: { groups: NavDef[] }) {
  const match = useRouteMatch();
  const { isMobile } = useSidebar();

  return (
    <>
      <SidebarGroup>
        <SidebarMenu>
          {groups.map((group) => (
            <Collapsible
              key={group.title}
              asChild
              defaultOpen={group.items.some((item) => {
                return (
                  (item.path &&
                    matchPath(match.url, {
                      path: item.path,
                      exact: item.exact,
                      strict: false,
                    })) ||
                  item.items?.some((subitem) =>
                    matchPath(match.url, {
                      path: subitem.path,
                      exact: subitem.exact,
                      strict: false,
                    })
                  )
                );
              })}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={group.title}>
                    <group.Icon />
                    <span>{group.title}</span>
                    <div className="flex items-center justify-end flex-1 gap-2">
                      <AlertBadge alertCountKeys={group.alertCountKeys} />
                      <ChevronRightIcon className="transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </div>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                {group.items?.length ? (
                  <CollapsibleContent>
                    <SidebarMenuSub className="mr-0 pr-0">
                      {group.items.map((item) =>
                        // A collapsible item (no route of its own) is the
                        // "…" dropdown itself. A definite item that also
                        // carries `items` (e.g. Checklists → Plantillas)
                        // navigates directly and gets a small "…" action
                        // next to it instead — see NavItemDefinite.
                        !item.to && item.items ? (
                          <DropdownMenu key={item.name}>
                            <DropdownMenuTrigger asChild>
                              <SidebarMenuButton className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                                {item.label ?? item.name}
                                <div className="flex items-center justify-end flex-1 gap-2">
                                  <AlertBadge
                                    alertCountKeys={item.alertCountKeys}
                                  />
                                  <MoreHorizontal className="size-4" />
                                </div>
                              </SidebarMenuButton>
                            </DropdownMenuTrigger>
                            {item.items?.length ? (
                              <DropdownMenuPortal>
                                <DropdownMenuContent
                                  side={isMobile ? 'bottom' : 'right'}
                                  align={isMobile ? 'end' : 'start'}
                                  className="min-w-56 rounded-lg"
                                >
                                  {item.items.map((subItem) => (
                                    <DropdownMenuItem
                                      key={subItem.name}
                                      asChild
                                    >
                                      {subItem.externalHref ? (
                                        <a
                                          href={subItem.externalHref}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          {subItem.label ?? subItem.name}
                                          <ExternalLink className="ml-auto size-3.5 text-muted-foreground" />
                                        </a>
                                      ) : (
                                        <NavLink to={subItem.to ?? ''}>
                                          {subItem.label ?? subItem.name}
                                        </NavLink>
                                      )}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenuPortal>
                            ) : null}
                          </DropdownMenu>
                        ) : (
                          <SidebarMenuSubItem key={item.name}>
                            <SidebarMenuSubButton
                              asChild
                              className={
                                item.items?.length ? 'pr-7' : undefined
                              }
                            >
                              <NavLink to={item.to ?? ''}>
                                <span>{item.label ?? item.name}</span>
                                <div className="flex items-center justify-end flex-1 gap-2">
                                  <AlertBadge
                                    alertCountKeys={item.alertCountKeys}
                                  />
                                </div>
                              </NavLink>
                            </SidebarMenuSubButton>
                            {item.items?.length ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <SidebarMenuAction
                                    aria-label={`Más opciones de ${item.label ?? item.name}`}
                                  >
                                    <MoreHorizontal className="size-4" />
                                  </SidebarMenuAction>
                                </DropdownMenuTrigger>
                                <DropdownMenuPortal>
                                  <DropdownMenuContent
                                    side={isMobile ? 'bottom' : 'right'}
                                    align={isMobile ? 'end' : 'start'}
                                    className="min-w-48 rounded-lg"
                                  >
                                    {item.items.map((subItem) => (
                                      <DropdownMenuItem
                                        key={subItem.name}
                                        asChild
                                      >
                                        {subItem.externalHref ? (
                                          <a
                                            href={subItem.externalHref}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            {subItem.label ?? subItem.name}
                                            <ExternalLink className="ml-auto size-3.5 text-muted-foreground" />
                                          </a>
                                        ) : (
                                          <NavLink to={subItem.to ?? ''}>
                                            {subItem.label ?? subItem.name}
                                          </NavLink>
                                        )}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenuPortal>
                              </DropdownMenu>
                            ) : null}
                          </SidebarMenuSubItem>
                        )
                      )}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                ) : null}
              </SidebarMenuItem>
            </Collapsible>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    </>
  );
}

const AlertBadge = ({ alertCountKeys }: { alertCountKeys?: string[] }) => {
  const filter = useOptionalFilterState();
  const companyId = filter.query?.company_id;
  const { data: alertsData } = useGetCompanyAlertsQuery({
    variables: companyId
      ? {
          context: {
            company_id: companyId,
            project_id: filter.query?.project_id,
            business_unit_id: filter.query?.business_unit_id,
          },
        }
      : undefined,
    pollInterval: 300 * 1000, // 5 min
    skip: !companyId,
  });
  const alertCounts = alertsData?.getCompanyAlerts;
  type AlertCountKey = keyof Omit<CompanyAlerts, '__typename'>;
  const count = alertCountKeys?.reduce((acc, key) => {
    if (alertCounts && (key as AlertCountKey) in alertCounts) {
      return acc + (alertCounts[key as AlertCountKey] as number);
    }
    return acc;
  }, 0);
  const hide = !alertCountKeys?.length || !count;
  return hide ? null : (
    <Badge
      className="shrink-0 text-xs px-0.5 py-1 rounded-lg shadow justify-self-end"
      variant="destructive"
    >
      {count}
    </Badge>
  );
};
